import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CalendarPlus, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DatesTab({ token, onChange }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [time, setTime] = useState("7:30pm – 9:15pm");
  const [capacity, setCapacity] = useState(40);
  const [price, setPrice] = useState("12.50");
  const [sortKey, setSortKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [editPriceId, setEditPriceId] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dates`, auth);
      setDates(data.dates || []);
    } catch {
      toast.error("Could not load dates.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) return toast.error("Enter a date label.");
    if (!title.trim()) return toast.error("Enter an event title.");
    if (!description.trim()) return toast.error("Enter a description.");
    if (!location.trim()) return toast.error("Enter a location.");
    const pricePence = Math.round(parseFloat(price) * 100);
    if (isNaN(pricePence) || pricePence < 0) return toast.error("Enter a valid price.");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/dates`, {
        label: label.trim(), title: title.trim(), description: description.trim(),
        location: location.trim(), time, capacity: parseInt(capacity, 10),
        price_pence: pricePence, sort_key: sortKey || label.trim(),
      }, auth);
      toast.success("Date added.");
      setLabel(""); setTitle(""); setDescription(""); setLocation(""); setSortKey(""); setCapacity(40); setPrice("12.50");
      load(); onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add date.");
    } finally {
      setSaving(false);
    }
  };

  const toggleSales = async (d) => {
    try {
      await axios.patch(`${API}/admin/dates/${d.id}`, { sales_closed: !d.sales_closed }, auth);
      toast.success(d.sales_closed ? "Sales reopened." : "Sales closed.");
      load(); onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update date.");
    }
  };

  const savePrice = async (d) => {
    const pence = Math.round(parseFloat(priceDraft) * 100);
    if (isNaN(pence) || pence < 0) return toast.error("Enter a valid price.");
    try {
      await axios.patch(`${API}/admin/dates/${d.id}`, { price_pence: pence }, auth);
      toast.success("Price updated.");
      setEditPriceId(null);
      load(); onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update price.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/dates/${id}`, auth);
      toast.success("Date removed.");
      load(); onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove date.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6" data-testid="dates-tab">
      <div className="ornate-frame bg-[var(--surface)] p-8">
        <h2 className="font-display text-3xl text-[var(--cream)] mb-1">Add a date</h2>
        <p className="font-body text-sm text-[var(--muted)] mb-6">Each date has its own capacity. Tickets show the date they're booked for.</p>
        <form onSubmit={add} className="space-y-5">
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Event title</label>
            <input data-testid="date-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Beer Explained" className="field-input" required />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Description</label>
            <textarea data-testid="date-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="A short description of the evening" rows={2} className="field-input resize-none" required />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Location</label>
            <input data-testid="date-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. The Horseshoe, Chipping Sodbury" className="field-input" required />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Date label</label>
            <input data-testid="date-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Thursday 3 September 2026" className="field-input" required />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Time</label>
            <input data-testid="date-time-input" value={time} onChange={(e) => setTime(e.target.value)} className="field-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Capacity (max people)</label>
              <input data-testid="date-capacity-input" type="number" min={1} max={1000} value={capacity} onChange={(e) => setCapacity(e.target.value)} className="field-input" />
            </div>
            <div>
              <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Price per ticket (£)</label>
              <input data-testid="date-price-input" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="field-input" />
            </div>
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Sort order (optional, e.g. 2026-09-03)</label>
            <input data-testid="date-sort-input" value={sortKey} onChange={(e) => setSortKey(e.target.value)} placeholder="YYYY-MM-DD" className="field-input" />
          </div>
          <button type="submit" data-testid="date-add-btn" disabled={saving}
            className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 uppercase tracking-[0.2em] text-xs font-semibold flex items-center justify-center gap-2 hover:bg-[var(--cream)] transition-colors duration-300 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />} Add date
          </button>
        </form>
      </div>

      <div className="ornate-frame bg-[var(--surface)] p-8">
        <h2 className="font-display text-3xl text-[var(--cream)] mb-6">Your dates</h2>
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" />
        ) : (
          <div className="space-y-3" data-testid="dates-list">
            {dates.map((d) => (
              <div key={d.id} className="flex items-center justify-between border border-[rgba(201,162,74,0.25)] px-4 py-3">
                <div>
                  {d.title && <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[var(--gold)]">{d.title}{d.location ? ` · ${d.location}` : ""}</p>}
                  <p className="font-serif-alt text-lg text-[var(--cream)]">{d.label}</p>
                  <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
                    {d.sold}/{d.capacity} sold · {d.remaining} left
                    {d.is_past && <span className="text-red-300"> · Past event</span>}
                    {!d.is_past && d.sales_closed && <span className="text-red-300"> · Sales closed</span>}
                  </p>
                  {editPriceId === d.id ? (
                    <span className="flex items-center gap-2 mt-2">
                      <input
                        data-testid={`date-price-edit-input-${d.id}`}
                        type="number" min={0} step="0.01" value={priceDraft}
                        onChange={(e) => setPriceDraft(e.target.value)}
                        className="field-input !py-1.5 !px-2 w-24 text-sm"
                        autoFocus
                      />
                      <button data-testid={`date-price-save-${d.id}`} onClick={() => savePrice(d)}
                        className="text-[10px] uppercase tracking-[0.15em] border border-[var(--gold)] text-[var(--gold)] px-2.5 py-1.5 hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
                        Save
                      </button>
                      <button onClick={() => setEditPriceId(null)}
                        className="text-[10px] uppercase tracking-[0.15em] text-[var(--muted)] hover:text-[var(--cream)] transition-colors duration-200">
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      data-testid={`date-price-edit-${d.id}`}
                      onClick={() => { setEditPriceId(d.id); setPriceDraft(((d.price_pence ?? 1250) / 100).toFixed(2)); }}
                      className="mt-1 inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-[0.15em] text-[var(--gold)] hover:text-[var(--cream)] transition-colors duration-200">
                      £{((d.price_pence ?? 1250) / 100).toFixed(2)} <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {!d.is_past && (
                    <button data-testid={`date-toggle-sales-${d.id}`} onClick={() => toggleSales(d)}
                      className={`text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors duration-200 ${d.sales_closed ? "border-[var(--gold)] text-[var(--gold)] hover:bg-[rgba(201,162,74,0.12)]" : "border-[rgba(201,162,74,0.35)] text-[var(--muted)] hover:text-red-300 hover:border-red-300"}`}>
                      {d.sales_closed ? "Reopen sales" : "Close sales"}
                    </button>
                  )}
                  <button data-testid={`date-delete-${d.id}`} onClick={() => remove(d.id)} title="Delete date"
                    className="text-[var(--muted)] hover:text-red-300 transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
